import { useBrandDetails } from 'dw-au-react-components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { CloseIcon } from '../../svg';
import { LoginForm } from '../Forms/LoginForm';
import { GuestDetailsForm } from '../Forms/GuestDetailsForm';
import CartDetails from './CartDetails';
import { PaymentForm } from '../Forms/PaymentForm';
import { Confirmation } from '../Confirmation';
import CreateProfileAddressObject from '../../Utils/ObjectUtils/CreateProfileAddressObject';
import CreateBillingObject from '../../Utils/ObjectUtils/CreateBillingObject';
import MemberCheckoutAction from '../../Actions/MemberCheckoutAction';
import GuestCheckoutAction from '../../Actions/GuestCheckoutAction';
import {
	MemberCheckout,
	MemberSelectShipping,
	MemberAddNewShipping,
	MemberBilling,
	MemberSelectPayment,
} from '../Member';
import { Loader } from '../../Components/Loader';
import { CartContext } from '../../Context/CartContext';
import { UserContext } from '../../Context/User';
import { ContentfulContext } from '../../Context/Contentful';
import ContiUpgrade from '../Addons/ContiUpgrade';
import AddonsUpgrade from '../Addons/AddonsUpgrade';
import UnlimitedUpgrade from '../Addons/UnlimitedUpgrade';
import GoogleAnalytics from '../../Utils/ObjectUtils/GoogleAnalytics';
import CreateEcommerceItem from '../../Utils/ObjectUtils/CreateEcommerceItem';
import usePostcodeOOA from '../../Hooks/usePostcodeOOA';

const StyledShoppingCartLayout = styled.div`
	.dmlp__cart__close {
		right: 36px;
		top: 36px;
	}
`;

function ShoppingCart({ entries }) {
	let fields = entries?.items[0]?.fields;
	let {
		ULItemcode,
		promoCode,
		promoCodeANI,
		bumpCodeMixedCase,
		bumpCodeRedCase,
		bumpCodeWhitesCase,
		enableAddons,
		enablePaypal,
		enableUnlimitedPopup,
		enableContiUpgrade,
		ULItemcodeNorthNZ = '',
		ULItemcodeSouthNZ = '',
		latestCatalogue,
		contiUpgradeVideo,
		contiUpgradeImage,
		contiUpgradeTitle,
		contiUpgradeCtaCopy,
		contiUpgradeCtaOfferCopy,
		contiUpgradeTc,
		contiUpgradeBenefits,

		contiUpgradeVideoV2,
		contiUpgradeImageV2,
		contiUpgradeTitleV2,
		contiUpgradeCtaCopyV2,
		contiUpgradeCtaOfferCopyV2,
		contiUpgradeTcV2,
		contiUpgradeBenefitsV2,

		unlimitedPopupTitle,
		unlimitedPopupCopy,
		unlimitedPopupTc,
		unlimitedLogo,
		unlimitedCtaCopy,
		unlimitedNoThanksCtaCopy,
		orderConfirmationTitle,
		unlForProspects,
		addonsTitle,
	} = fields;
	const [backToStep, setBackToStep] = useState('');
	const [shippingFormData, setShippingFormData] = useState({});
	const [paymentFormData, setPaymentFormData] = useState({});
	const [editMemberShippingData, setEditMemberShippingData] = useState({});
	const [editMemberBillingData, setEditMemberBillingData] = useState({});
	const [newCard, setNewCard] = useState(null);
	const [paymentMethod, setPaymentMethod] = useState('card');
	const [loading, setLoading] = useState(false);
	const [orderConfirmationData, setOrderConfirmationData] = useState('');
	const [addUnlimitedToCart, setAddUnlimitedToCart] = useState(false);
	const [addContiToCart, setAddContiToCart] = useState(false);
	const [postCode, setPostCode] = useState(null);
	const [isMemberCheckout, setIsMemberCheckout] = useState(false);
	const brandData = useBrandDetails();
	const [contiDetails, setContiDetails] = useState({
		video: contiUpgradeVideo,
		image: contiUpgradeImage,
		title: contiUpgradeTitle,
		ctaCopy: contiUpgradeCtaCopy,
		ctaOfferCopy: contiUpgradeCtaOfferCopy,
		tc: contiUpgradeTc,
		benefits: contiUpgradeBenefits,
	});

	const {
		userData,
		fetchUserData,
		userhasUnlimited,
		userhasWineplan,
		logoutUser,
		fetchThirdPartyList,
	} = useContext(UserContext);
	const {
		handleToggleCart,
		toggleCart,
		step,
		setStep,
		cartData,
		couponCode,
		velocityNumber,
		removeVelocityNumber,
		removeCouponCode,
		setGuestShipping,
	} = useContext(CartContext);
	const {
		allowExistingCustomer,
		products,
		showThirdParty,
		showVouchers,
		offerThirdParty,
		lpDefaults,
	} = useContext(ContentfulContext);
	let userEmail = userData?.summaryDetails?.emailAddress;
	let paymentWallet = userData?.trustedDetails?.paymentWallet;
	let billingAddress = userData?.trustedDetails?.billingAddress;
	let shippingAddress = userData?.trustedDetails?.shippingAddress;
	let isLoggedIn = userData && userData.securityStatus >= 4;

	const [message, setMessage] = useState(null);
	const [contiUpgradePresented, setContiUpgradePresented] = useState(false);
	const [unlimitedPresented, setUnlimtedPresented] = useState(false);
	const [addonsPresented, setAddonsPresented] = useState(false);
	const [cvv, setCVV] = useState(null);
	const [selectedAddon, setSelectedAddon] = useState(null);
	const [businessPartner, setBusinessPartner] = useState('WP');
	const [receiveEmail, setReceiveEmail] = useState(false);
	const [isPayemntUpdated, setIsPayemntUpdated] = useState(false);
	const [addonType, setAddonType] = useState('mixed');
	const isOOAPostCode = usePostcodeOOA(postCode, brandData.country); // Check if Out of Area Postcode
	// Guards purchase/account events against duplicate pushes per order
	const trackedOrderRef = useRef(null);

	useEffect(() => {
		if (isPayemntUpdated) {
			handleUpgrades();
		}
		setIsPayemntUpdated(false);
	}, [isPayemntUpdated]);

	// view_cart: mini-cart opened with items (basket view); re-fires on cart change.
	// Skips the post-order clear where orderDetails is still in session.
	useEffect(() => {
		if (
			toggleCart &&
			cartData.length > 0 &&
			!sessionStorage.getItem('orderDetails')
		) {
			const items = cartData.map((ci) =>
				CreateEcommerceItem(ci.product, ci.quantity || 1)
			);
			GoogleAnalytics.setEcommerce('view_cart', items);
		}
	}, [toggleCart, cartData]);

	useEffect(() => {
		if (step === 0) {
			setGuestShipping(false);
		}
	}, [step]);

	useEffect(() => {
		if (shippingAddress && isLoggedIn) {
			const filteredAddress = getDefaultMemberShipping();
			setPostCode(filteredAddress?.postalCode);
		}
	}, [shippingAddress]);

	useEffect(() => {
		if (enableAddons && cartData.length > 0) {
			let colourName = cartData[0]?.product?.colourName;

			if (colourName) {
				if (colourName === 'Red' || colourName === 'White') {
					colourName = colourName + 's';
				}

				setAddonType(colourName?.toLowerCase());
			}

			let addonBulkItem = products.filter(({ product }) => {
				return (
					product?.itemCode?.charAt(0) !== 'F' &&
					product?.itemCode?.charAt(0) !== 'J' &&
					product?.itemCode !== cartData[0].itemCode &&
					(product?.name?.toLowerCase().indexOf('addon') > -1 ||
						product?.name?.toLowerCase().indexOf('add-on') > -1 ||
						product?.name?.toLowerCase().indexOf('add on') > -1) &&
					product?.colourName === cartData[0]?.product?.colourName
				);
			});

			if (addonBulkItem.length > 0) {
				setSelectedAddon(addonBulkItem[0]);
			}
		}
	}, [products, cartData]);

	useEffect(() => {
		let mounted = true;

		// Fetch Business Partner ID
		async function fetchProgramId() {
			await axios
				.get(`/api/user/loyaltyprogram/program/list/${brandData.sunbrandKey}`)
				.then((res) => {
					if (mounted) {
						setBusinessPartner(
							res?.data?.response?.thirdPartyDetails[0]?.programId
						);
					}
				});
		}
		if (offerThirdParty === 'RAA') {
			setBusinessPartner(offerThirdParty);
		} else {
			fetchProgramId();
		}

		return () => (mounted = false);
	}, []);

	const handleShippingFormSubmission = (
		guestShippingFormData,
		fromStep = ''
	) => {
		setShippingFormData(guestShippingFormData);
		setPostCode(guestShippingFormData?.postalCode);
		setStep(3);
		setBackToStep(fromStep);
	};

	const handlePaymentFormSubmission = (PaymentData) => {
		if (isLoggedIn && allowExistingCustomer) {
			if (PaymentData === 'paypal') {
				setPaymentMethod('paypal');
			} else {
				setNewCard(PaymentData);
			}
			setStep(5);
		} else {
			setPaymentFormData(PaymentData);
			setIsPayemntUpdated(true);
		}
	};

	const handleMemberShipping = (memberAddress = '', fromStep = '') => {
		setEditMemberShippingData(memberAddress);
		setBackToStep(fromStep);
		setStep(7);
	};

	const handleMemberBilling = (memberAddress = '', fromStep = '') => {
		setEditMemberBillingData(memberAddress);
		setBackToStep(fromStep);
		setStep(8);
	};

	const handleMemberAddPayment = (paymentData = '', fromStep = '') => {
		setPaymentFormData(paymentData);
		setBackToStep(fromStep);
		setStep(3);
	};

	// API call to Update Existing Address
	const updateExistingShippingAddress = (
		data,
		addressId,
		makeDefaultAddress = false
	) => {
		let finalShippingObjectForAPICall = CreateProfileAddressObject(
			brandData.country,
			userEmail,
			data,
			addressId,
			makeDefaultAddress
		);

		setLoading(true);
		axios
			.put(
				`/api/user/shipping/address/${addressId}`,
				finalShippingObjectForAPICall
			)
			.then(() => {
				setLoading(false);
				fetchUserData();
				setMessage({
					class: 'alert-success',
					message: 'You have successfully updated your default address.',
				});
				setTimeout(() => {
					setMessage(null);
					setStep(5);
				}, 2000);
			})
			.catch(() => {
				setLoading(false);
				setMessage({
					class: 'alert-danger',
					message: 'Something went wrong!',
				});
				setTimeout(() => {
					setMessage(null);
				}, 2000);
			});
	};

	// API call to make default Address
	const handleMakeDefaultAddress = (addressId) => {
		setLoading(true);
		axios
			.post(`/api/user/shipping/address/${addressId}/options`, {
				addressOptions: ['DEFAULT_ADDRESS'],
			})
			.then(() => {
				setLoading(false);
				fetchUserData();
				setMessage({
					class: 'alert-success',
					message: 'You have successfully updated your default address.',
				});
				setTimeout(() => {
					setMessage(null);
					setStep(5);
				}, 2000);
			})
			.catch(() => {
				setLoading(false);
				setMessage({
					class: 'alert-danger',
					message: 'Something went wrong!',
				});
				setTimeout(() => {
					setMessage(null);
				}, 2000);
			});
	};

	// API call to Add New Address
	const addShippingAddressToProfile = (data) => {
		let finalShippingObjectForAPICall = CreateProfileAddressObject(
			brandData.country,
			userEmail,
			data
		);
		axios
			.post('/api/user/shipping/addresses', finalShippingObjectForAPICall)
			.then(() => {
				setLoading(false);
				fetchUserData();
				setMessage({
					class: 'alert-success',
					message: 'Shipping Details Added',
				});
				setTimeout(() => {
					setMessage(null);
					setStep(5);
				}, 2000);
			})
			.catch(() => {
				setLoading(false);
				setMessage({
					class: 'alert-danger',
					message: 'Something went wrong!',
				});
				setTimeout(() => {
					setMessage(null);
				}, 2000);
			});
	};

	// Billing Area

	// API call to Update Billing Address
	const updateProfileBillingAddress = (billingAddress) => {
		let finalBillingObjectForAPICall = CreateBillingObject(
			brandData.country,
			userEmail,
			billingAddress
		);

		setLoading(true);
		axios
			.put('/api/user/billing/address', finalBillingObjectForAPICall)
			.then(() => {
				setLoading(false);
				fetchUserData();
				setMessage({
					class: 'alert-success',
					message: 'Billing Details updated',
				});
				setTimeout(() => {
					setMessage(null);
					setStep(5);
				}, 2000);
			})
			.catch(() => {
				setLoading(false);
				setMessage({
					class: 'alert-danger',
					message: 'Something went wrong!',
				});
				setTimeout(() => {
					setMessage(null);
				}, 2000);
			});
	};

	const submitOrder = (finalObjectForApiCall) => {
		setLoading(true);

		// Place-order click intent (parity with main site Checkout_Click_PlaceOrder)
		GoogleAnalytics.setEventTrackingGA4('Checkout_Click_PlaceOrder');

		const section = document.querySelector('.dmlp__cart__inner');
		section?.scrollIntoView({ behavior: 'smooth', block: 'start' });

		let stateCode =
			finalObjectForApiCall?.shippingDetails[0]?.shippingDetail?.shippingAddress
				?.address?.stateCode;
		if (brandData.country === 'AU' && stateCode.toLowerCase() === 'nt') {
			setLoading(false);
			setMessage({
				class: 'alert-danger',
				message:
					'Due to changes to the alcohol laws in the Northern Territory we apologise but we are no longer able to ship wines to this state',
			});
		} else {
			setIsMemberCheckout(isLoggedIn ? true : false);
			axios
				.post('/api/checkout/orders/v2', finalObjectForApiCall)
				.then(function (response) {
					setOrderConfirmationData(response?.data?.response);
					sessionStorage.setItem(
						'orderDetails',
						JSON.stringify(response?.data?.response?.order)
					);

					if (
						response?.data?.response?.webRedirectDetails?.redirectRequired &&
						response?.data?.response?.webRedirectDetails
							?.secureResponseSuccessURL
					) {
						// PayPal is out of scope for order/purchase events (confirmed with business).
						// PayPal redirects away to a separate confirmation page.
						console.warn(
							'[LP-GTM][PayPal - out of scope] purchase/Checkout_NewAccount/email NOT fired here',
							{
								orderId: response?.data?.response?.order?.id,
								redirectUrl:
									response?.data?.response?.webRedirectDetails
										?.secureResponseSuccessURL,
								isLoggedIn,
							}
						);
						window.location.href =
							response?.data?.response?.webRedirectDetails?.secureResponseSuccessURL;
					} else {
						const confirmedOrder = response?.data?.response?.order;
						const orderId = confirmedOrder?.id;
						const orderEmail =
							finalObjectForApiCall?.accountRegistrationPreferences?.email ||
							shippingFormData?.email ||
							userEmail;

						if (orderId && trackedOrderRef.current !== orderId) {
							trackedOrderRef.current = orderId;

							GoogleAnalytics.setOrderDataLayer(orderId);
							GoogleAnalytics.setPageView(
								window.location.pathname,
								'Order Confirmation'
							);

							// Guarantee email in dataLayer for guest/new customers
							if (orderEmail) {
								GoogleAnalytics.setDataLayer({ email: orderEmail });
							}

							// Guest orders create the account atomically with the order
							if (!isLoggedIn) {
								GoogleAnalytics.setEventTrackingGA4('Checkout_NewAccount');
							}
						} else {
							console.log('[LP-GTM] duplicate order event skipped', orderId);
						}

						if (confirmedOrder && userData && cartData) {
							// eslint-disable-next-line no-undef
							AdobeDLFunctions.dlPageInit(userData, cartData, confirmedOrder);
						}
						setLoading(false);
						if (!isLoggedIn) {
							fetchUserData();
						}
						setShippingFormData({});
						setPaymentFormData({});
						removeVelocityNumber();
						removeCouponCode();
						fetchThirdPartyList();
						setStep(4);
					}
				})
				.catch(function (error) {
					setLoading(false);
					setMessage({
						class: 'alert-danger',
						message: error?.response?.data?.statusMessage
							? error.response.data.statusMessage
							: 'Something went wrong! Please try again',
					});
					// setTimeout(() => {
					// 	setMessage(null);
					// }, 5000);
				});
		}
	};

	const handleGuestCheckout = (
		PaymentData,
		bumpCode = addContiToCart,
		addon = selectedAddon,
		UnlimitedItemCode = addUnlimitedToCart
	) => {
		setMessage(null);
		let finalObjectForApiCall = GuestCheckoutAction(
			shippingFormData,
			PaymentData,
			brandData,
			cartData,
			promoCode ?? promoCodeANI,
			bumpCode,
			addon,
			couponCode,
			velocityNumber,
			businessPartner,
			receiveEmail,
			UnlimitedItemCode
		);

		if (finalObjectForApiCall) {
			submitOrder(finalObjectForApiCall);
		}
	};

	const getDefaultMemberShipping = () => {
		let filteredAddress = null;

		if (shippingAddress) {
			filteredAddress = shippingAddress.filter((address, index) => {
				return address.defaultAddress === true;
			});

			if (!filteredAddress.length > 0) {
				filteredAddress = shippingAddress.filter((address, index) => {
					return index === 0;
				});
			}

			filteredAddress = filteredAddress[0];
		}

		return filteredAddress;
	};

	// Member checkout
	const handleMemberCheckout = (
		UnlimitedItemCode = addUnlimitedToCart,
		bumpCode = addContiToCart,
		addon = selectedAddon
	) => {
		setMessage(null);
		if (
			billingAddress &&
			shippingAddress &&
			(paymentWallet || newCard || paymentMethod === 'paypal')
		) {
			let finalPaymentData, filteredAddress;

			if (paymentMethod !== 'paypal' && newCard) {
				finalPaymentData = [newCard];
			} else if (paymentMethod === 'paypal') {
				finalPaymentData = 'paypal';
			} else {
				finalPaymentData = paymentWallet.filter((payment, index) => {
					return payment.defaultPayment === true;
				});

				if (!finalPaymentData.length > 0) {
					finalPaymentData = paymentWallet.filter((payment, index) => {
						return index === 0;
					});
				}
			}

			finalPaymentData = finalPaymentData[0];
			filteredAddress = getDefaultMemberShipping();

			let isNewCard = newCard ? true : false;

			let finalObjectForApiCall = MemberCheckoutAction(
				filteredAddress,
				billingAddress,
				finalPaymentData,
				brandData,
				userEmail,
				cvv,
				isNewCard,
				paymentMethod,
				cartData,
				UnlimitedItemCode,
				promoCode ?? promoCodeANI,
				bumpCode,
				addon,
				couponCode,
				velocityNumber,
				businessPartner,
				receiveEmail
			);

			if (finalObjectForApiCall) {
				submitOrder(finalObjectForApiCall);
			}
		} else {
			setMessage({
				class: 'alert-danger',
				message: 'Something went wrong!',
			});
			setTimeout(() => {
				setMessage(null);
			}, 2000);
		}
	};

	const [loginEmail, setLoginEmail] = useState(null);
	const handleEmailIsRecognized = (email) => {
		setLoginEmail(email);
	};

	const handleUnlimited = (itemCode = '') => {
		if (itemCode) {
			setAddUnlimitedToCart(itemCode);
		}
		// setStep(1);
		handleUpgrades(false, itemCode);
	};

	const handleContiUpgrade = (isContiUpgrade = false) => {
		let bumpCode;
		if (isContiUpgrade) {
			let name = cartData[0].product.name.toLowerCase(),
				colourId = cartData[0].product?.colourId?.toLowerCase();
			const defaultContiBumpCode = lpDefaults?.lpContiBumpCode?.items;
			bumpCode = defaultContiBumpCode
				.filter((item) => {
					return name.indexOf(item.type) > -1 || name.indexOf(item.type2) > -1;
				})
				.map((item) => {
					return item.bumpCode;
				})[0];

			if (!bumpCode) {
				if (
					colourId &&
					(colourId.indexOf('red') > -1 ||
						colourId.indexOf('white') > -1 ||
						colourId.indexOf('mixed') > -1)
				) {
					bumpCode = defaultContiBumpCode
						.filter((item) => {
							return (
								colourId.indexOf(item.type) > -1 ||
								colourId.indexOf(item.type2) > -1
							);
						})
						.map((item) => {
							return item.bumpCode;
						})[0];
				} else {
					bumpCode = defaultContiBumpCode
						.filter((item) => {
							return item.type === 'mixed';
						})
						.map((item) => {
							return item.bumpCode;
						})[0];
				}
			}

			if (
				bumpCodeWhitesCase &&
				(name.indexOf('white') > -1 ||
					name.indexOf('wht') > -1 ||
					colourId.indexOf('white') > -1 ||
					colourId.indexOf('wht') > -1)
			) {
				bumpCode = bumpCodeWhitesCase;
			} else if (
				bumpCodeRedCase &&
				(name.indexOf('red') > -1 || colourId.indexOf('red') > -1)
			) {
				bumpCode = bumpCodeRedCase;
			} else if (
				bumpCodeMixedCase &&
				(name.indexOf('mixed') > -1 ||
					name.indexOf('mix') > -1 ||
					colourId.indexOf('mixed') > -1 ||
					colourId.indexOf('mix') > -1)
			) {
				bumpCode = bumpCodeMixedCase;
			}

			if (bumpCode) {
				setAddContiToCart(bumpCode);
			}
		}
		handleUpgrades(false, addUnlimitedToCart, bumpCode);
	};

	const handleAddonsUpgrade = (isAddonsUpgrade = false) => {
		if (isAddonsUpgrade && selectedAddon) {
			handleUpgrades(selectedAddon);
		} else {
			handleUpgrades(false);
			setSelectedAddon(null);
		}
	};

	/**
	 * Added Adobe DL event after upgrade popup displayed
	 */
	const adobeDLUpgradePopupDisplay = () => {
		var AdobeDLClickEventObj = {
			clickEvent: 'RecCheckoutUgPopup',
			clickDescription: 'rec:checkout:ug:popup',
			name: 'Upgrade to wine plan pop up displayed',
			clickLocation: 'rec checkout upgrade',
			clickText: 'Popup',
			clickAction: 'pop up displayed',
		};

		// eslint-disable-next-line no-undef
		AdobeDLFunctions.clickEventDL(
			AdobeDLClickEventObj.clickEvent,
			AdobeDLClickEventObj
		);
	};

	const handleUpgrades = (
		addon = selectedAddon,
		UnlimitedItemCode = addUnlimitedToCart,
		bumpCode = addContiToCart
	) => {
		if (unlForProspects) {
			if (
				!unlimitedPresented &&
				!isOOAPostCode &&
				(!isLoggedIn || !userhasUnlimited)
			) {
				setStep(11);
				setUnlimtedPresented(true);
			} else if (
				enableContiUpgrade &&
				!contiUpgradePresented &&
				(!isLoggedIn || !userhasWineplan)
			) {
				setStep(10);
				setContiDetails({
					video: isOOAPostCode ? contiUpgradeVideo : contiUpgradeVideoV2,
					image: isOOAPostCode ? contiUpgradeImage : contiUpgradeImageV2,
					title: isOOAPostCode ? contiUpgradeTitle : contiUpgradeTitleV2,
					ctaCopy: isOOAPostCode ? contiUpgradeCtaCopy : contiUpgradeCtaCopyV2,
					ctaOfferCopy: isOOAPostCode
						? contiUpgradeCtaOfferCopy
						: contiUpgradeCtaOfferCopyV2,
					tc: isOOAPostCode ? contiUpgradeTc : contiUpgradeTcV2,
					benefits: isOOAPostCode
						? contiUpgradeBenefits
						: contiUpgradeBenefitsV2,
				});
				setContiUpgradePresented(true);
				adobeDLUpgradePopupDisplay();
			} else if (!addonsPresented && enableAddons && selectedAddon) {
				setStep(12);
				setAddonsPresented(true);
			} else {
				if (isLoggedIn) {
					setStep(5);
					handleMemberCheckout(UnlimitedItemCode, bumpCode, addon);
				} else {
					setStep(3);
					handleGuestCheckout(
						paymentFormData,
						bumpCode,
						addon,
						UnlimitedItemCode
					);
				}
			}
		} else {
			if (
				enableContiUpgrade &&
				!contiUpgradePresented &&
				(!userhasWineplan || !isLoggedIn)
			) {
				setStep(10);
				setContiUpgradePresented(true);
				adobeDLUpgradePopupDisplay();
			} else if (
				enableUnlimitedPopup &&
				!isOOAPostCode &&
				!unlimitedPresented &&
				!userhasUnlimited &&
				isLoggedIn
			) {
				setStep(11);
				setUnlimtedPresented(true);
			} else if (!addonsPresented && enableAddons && selectedAddon) {
				setStep(12);
				setAddonsPresented(true);
			} else {
				if (isLoggedIn) {
					setStep(5);
					handleMemberCheckout(UnlimitedItemCode, bumpCode, addon);
				} else {
					setStep(3);
					handleGuestCheckout(paymentFormData, bumpCode, addon);
				}
			}
		}
	};

	// Check email already exist and redirect to login/Guest Shipping
	const checkEmailExistance = (emailCheck) => {
		if (emailCheck) {
			axios({
				method: 'post',
				url: '/api/account/email',
				data: {
					email: emailCheck,
				},
			})
				.then((res) => {
					const response = res.data;
					if (response.statusCode === 0) {
						setLoginEmail(emailCheck);
						setStep(1);
						GoogleAnalytics.setEventTrackingGA4(
							'Contentful_Rec_Checkout_Existing'
						);
					}
				})
				.catch(() => {
					setGuestShipping(true);
					GoogleAnalytics.setEventTrackingGA4('Contentful_Rec_Checkout_New');
					setShippingFormData({
						...shippingFormData,
						email: emailCheck,
					});
				});
		}
	};

	return (
		<StyledShoppingCartLayout>
			<div
				onClick={() => handleToggleCart()}
				className={`dmlp__cart__overlay position-fixed ${
					toggleCart ? 'open' : ''
				}`}
			></div>
			<div className={`dmlp__cart position-fixed ${toggleCart ? 'open' : ''}`}>
				<div className='dmlp__cart__inner position-relative p-40'>
					{loading && <Loader extraClassName='overlay' />}
					<>
						{step !== 4 && step !== 10 && step !== 11 && step !== 12 && (
							<span
								className='dmlp__cart__close dmlp__recruitmentTemplate__close position-absolute text-center'
								title='Click to close the Shopping Cart'
								onClick={() => handleToggleCart()}
							>
								<CloseIcon />
							</span>
						)}
						{step === 0 && (
							<CartDetails
								setStep={setStep}
								isLoggedIn={isLoggedIn}
								allowExistingCustomer={allowExistingCustomer}
								showThirdParty={showThirdParty}
							/>
						)}
						{step === 1 && (
							<LoginForm
								setStep={setStep}
								defaultValues={{ email: loginEmail }}
								setGuestShipping={setGuestShipping}
							/>
						)}
						{step === 2 && (
							<GuestDetailsForm
								step={step}
								setStep={setStep}
								handleFormSubmission={handleShippingFormSubmission}
								defaultValues={shippingFormData}
								onEmailValidation={handleEmailIsRecognized}
								allowExistingCustomer={allowExistingCustomer}
								setLoginEmail={setLoginEmail}
								checkEmailExistance={checkEmailExistance}
							/>
						)}
						{step === 3 && (
							<PaymentForm
								isLoggedIn={isLoggedIn}
								step={step}
								setStep={setStep}
								handleFormSubmission={handlePaymentFormSubmission}
								defaultValues={paymentFormData}
								backToStep={backToStep}
								message={message}
								enablePaypal={enablePaypal}
								allowExistingCustomer={allowExistingCustomer}
								showVouchers={showVouchers}
								setReceiveEmail={setReceiveEmail}
								receiveEmail={receiveEmail}
							/>
						)}
						{step === 4 && (
							<Confirmation
								step={step}
								setStep={setStep}
								isMemberCheckout={isMemberCheckout}
								shippingFormData={shippingFormData}
								paymentFormData={paymentFormData}
								orderConfirmationData={orderConfirmationData}
								isLoggedIn={isLoggedIn}
								latestCatalogue={latestCatalogue}
								title={orderConfirmationTitle}
							/>
						)}
						{step === 5 && (
							<MemberCheckout
								setStep={setStep}
								userData={userData}
								handleMemberShipping={handleMemberShipping}
								handleMemberBilling={handleMemberBilling}
								showNewCard={newCard}
								paymentMethod={paymentMethod}
								handleContiUpgrade={handleContiUpgrade}
								// handleMemberCheckout={handleMemberCheckout}
								handleMemberCheckout={handleUpgrades}
								message={message}
								setMessage={setMessage}
								setCVV={setCVV}
								showVouchers={showVouchers}
								logoutUser={logoutUser}
								setReceiveEmail={setReceiveEmail}
								receiveEmail={receiveEmail}
							/>
						)}
						{step === 6 && (
							<MemberSelectShipping
								setStep={setStep}
								userData={userData}
								message={message}
								handleMemberShipping={handleMemberShipping}
								handleMakeDefaultAddress={handleMakeDefaultAddress}
							/>
						)}
						{step === 7 && (
							<MemberAddNewShipping
								setStep={setStep}
								userData={userData}
								defaultValues={editMemberShippingData}
								backToStep={backToStep}
								updateExistingShippingAddress={updateExistingShippingAddress}
								addShippingAddressToProfile={addShippingAddressToProfile}
								message={message}
							/>
						)}
						{step === 8 && (
							<MemberBilling
								setStep={setStep}
								userData={userData}
								defaultValues={editMemberBillingData}
								handleMemberFormSubmission={updateProfileBillingAddress}
								message={message}
							/>
						)}
						{step === 9 && (
							<MemberSelectPayment
								setStep={setStep}
								userData={userData}
								handleMemberAddPayment={handleMemberAddPayment}
								fetchUserData={fetchUserData}
								setNewCard={setNewCard}
								paymentMethod={paymentMethod}
								setPaymentMethod={setPaymentMethod}
								setLoading={setLoading}
								enablePaypal={enablePaypal}
								handleFormSubmission={handlePaymentFormSubmission}
							/>
						)}
						{step === 10 && (
							<ContiUpgrade
								handleContiUpgrade={handleContiUpgrade}
								brand={brandData.tag}
								contiDetails={contiDetails}
								lpDefaults={lpDefaults}
							/>
						)}
						{step === 11 && (
							<UnlimitedUpgrade
								handleUnlimited={handleUnlimited}
								brandData={brandData}
								ULItemcode={ULItemcode}
								ULItemcodeNorthNZ={ULItemcodeNorthNZ}
								ULItemcodeSouthNZ={ULItemcodeSouthNZ}
								title={unlimitedPopupTitle}
								copy={unlimitedPopupCopy}
								tc={unlimitedPopupTc}
								logo={unlimitedLogo}
								unlForProspects={unlForProspects}
								ctaCopy={unlimitedCtaCopy}
								noThanksCTACopy={unlimitedNoThanksCtaCopy}
								promoCode={promoCode ?? promoCodeANI}
							/>
						)}
						{step === 12 && (
							<AddonsUpgrade
								brand={brandData.tag}
								country={brandData.country}
								handleAddonsUpgrade={handleAddonsUpgrade}
								type={addonType}
								promoCode={promoCode ?? promoCodeANI}
								addonsTitle={addonsTitle}
							/>
						)}
					</>
				</div>
			</div>
		</StyledShoppingCartLayout>
	);
}

export default ShoppingCart;
